"use client";

import React from "react";
import { Box, Typography, Stack, Radio } from "@mui/material";
import {
  ColletteButton,
  ColletteChip,
  ColletteTextButton
} from "@collette-travel/collette-common-controls";
import { useTranslations } from "next-intl";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";

interface DepartureDateRowMobileProps {
  index?: number;
  tourDate: TourDate;
  isSelected: boolean;
  onSelect: (tourDate: TourDate) => void;
  onRequestQuote?: (tourDate: TourDate) => void;
  onViewItinerary?: (tourDate: TourDate) => void;
  onBookNow?: (tourDate: TourDate) => void;
  onHover?: (tourDate: TourDate) => void;
  lastItem: boolean;
  hasSpecialEvent: boolean;
  hasReverseItinerary: boolean;
  isSoldOut: boolean;
  isCallToBook: boolean;
  isSeatsRemainingThreshold: boolean;
  showStatusMessage: boolean;
  hasOffer: boolean;
  formattedDate: string;
  offerBadgeText: string;
}

const DepartureDateRow = ({
  index,
  tourDate,
  isSelected,
  onSelect,
  onRequestQuote,
  onViewItinerary,
  onBookNow,
  onHover,
  lastItem,
  hasSpecialEvent,
  hasReverseItinerary,
  isSoldOut,
  isCallToBook,
  isSeatsRemainingThreshold,
  showStatusMessage,
  formattedDate,
  offerBadgeText,
  hasOffer
}: DepartureDateRowMobileProps) => {
  const t = useTranslations("PDP");

  return (
    <Box
      role="button"
      tabIndex={-1}
      onMouseEnter={() => onHover?.(tourDate)}
      onClick={() => onSelect(tourDate)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(tourDate);
        }
      }}
      sx={{
        backgroundColor: isSelected ? "background.cool_strong" : "transparent",
        border: isSelected ? "2px solid" : "none",
        borderColor: "primary.main",
        borderBottom: (theme) => !isSelected ? `1px solid ${theme.palette.divider}` : undefined,
        padding: "1.2rem",
        paddingBottom: tourDate.statusMessage && "1.5rem",
        cursor: "pointer",
        display: { xs: "flex", sm: "none" },
        flexDirection: "column",
        gap: "0",
        borderBottomLeftRadius: lastItem ? "2rem" : 0,
        borderBottomRightRadius: lastItem ? "2rem" : 0,
        position: "relative"
      }}
      data-testid={`departure-date-row-mobile_${index}`}
    >
      {/* Main Row Content - Date + Price always horizontal */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        {/* Left section: Date + Price */}
        <Stack direction="row" alignItems="start" flex={1} maxWidth={ tourDate.strikeOutPrice && hasOffer ? "65%" : "58%"}>
          {/* Radio + Date */}
          <Stack
            direction="row"
            alignItems="center"
            width="100%"
          >
            <Radio
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelect(tourDate)}
              data-testid={`departure-date_${index}_radio`}
              sx={{
                color: "primary.main",
                "&.Mui-checked": {
                  color: "primary.main"
                }
              }}
            />
            <Stack
              sx={{
                alignItems: "flex-start",
                justifyContent: "space-between",
                width: "100%",
                flexDirection: "column"
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  color: isSoldOut ? "text.disabled" : "text.primary",
                  width: "18rem"
                }}
                data-testid={`departure-date_${index}`}
              >
                {formattedDate}
              </Typography>
              {/* Price */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "secondary.main",
                    textAlign: "left",
                    minWidth: "6rem"
                  }}
                  data-testid={`departure-date_${index}_tour-price`}
                >
                  {tourDate.offerPrice || tourDate.doublePrice}
                </Typography>
                {tourDate.strikeOutPrice && hasOffer && (
                  <Typography
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      color: "text.disabled",
                      textDecoration: "line-through",
                      visibility: tourDate.strikeOutPrice ? "visible" : "hidden"
                      }}
                      data-testid={`departure-date_${index}_tour-strikeout-price`}
                  >
                    {tourDate.strikeOutPrice}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "30%", justifyContent:"flex-end"}}>
          <Box display="flex" width="100%" flexDirection="row" justifyContent="flex-end" alignItems="center">
            {/* Offer Badge */}
            {hasOffer && offerBadgeText && (
              <Box
                sx={{
                  minWidth: "fit-content",
                  justifyContent: "center"
                }}
              >
                <ColletteChip
                  color="warning"
                  testId={`departure-date_${index}_offer-badge`}
                  label={offerBadgeText}
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    borderRadius: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Stack>

      {/* Mobile: Status indicators below date (with 40px indent) */}
      {((tourDate.statusMessage && showStatusMessage) || hasSpecialEvent) && (
        <Stack
          sx={{
            display: "block",
            marginLeft: "4rem",
            marginTop: "1.2rem"
          }}
        >
          {/* Status Message - Mobile */}
          {tourDate.statusMessage && showStatusMessage && (
            <Typography
              sx={{
                fontSize: "1.4rem",
                fontWeight: 700,
                color: isSoldOut || isSeatsRemainingThreshold ? "error.main" : "secondary.main",
                textDecoration: (isCallToBook && !isSeatsRemainingThreshold) ? "underline" : "none",
                marginBottom: 0
              }}
              data-testid={`departure-date_${index}_status`}
            >
              {tourDate.statusMessage}
            </Typography>
          )}

          {/* Reverse Itinerary - Mobile (inline) */}
          {hasReverseItinerary && (
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 500,
                color: "primary.main"
              }}
              data-testid={`departure-date_${index}_reverse-itinerary-tour`}
            >
              {t("reverseItinerary")}
            </Typography>
          )}

          {/* Special Event - Mobile (inline) */}
          {hasSpecialEvent && (
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={0.5} 
              position="relative"
              marginTop="1.2rem"
            >
              <Box
                component="img"
                src="/images/inclusions/special-event.svg"
                alt={t("specialEvent")}
                sx={{ width: "2.3102rem", height: "1.272rem", flexShrink: 0 }}
                data-testid={`departure-date_${index}_special-event-image`}
              />
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 500,
                  color: "primary.main"
                }}
                data-testid={`departure-date_${index}_special-event-label`}
              >
                {t("specialEvent")}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Expanded View - Single Supplement and CTAs */}
      {isSelected && (
        <>
          {/* Single Supplement */}
          <Stack direction="row" width="100%">
            <Stack direction="row"
              data-testid="single-supplement-section"
              sx={{ 
                marginLeft: "0",
                display:"flex",
                flexDirection:"row",
                alignItems:"center",
                justifyContent: "flex-start",
                marginTop: "2rem",
                width:"100%",
                flex: 1
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  color: "text.primary",
                  paddingRight: "1.2rem"
                }}
                data-testid={`departure-date_${index}_single-supplement-title`}
              >
                {t("singleSupplement")}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "secondary.main",
                    minWidth: "6rem"
                  }}
                  data-testid={`departure-date_${index}_single-supplement-price`}
                >
                  {tourDate.singlePrice}
                </Typography>
                {tourDate.singleStrikeOutPrice && hasOffer && (
                  <Typography
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      color: "text.secondary",
                      textDecoration: "line-through",
                      visibility: tourDate.singleStrikeOutPrice ? "visible" : "hidden"
                    }}
                    data-testid={`departure-date_${index}_single-supplement-strikeout-price`}
                  >
                    {tourDate.singleStrikeOutPrice}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* CTAs */}
          <Stack
            gap="1.2rem"
            sx={{
              width: "100%",
              marginTop: "1.6rem",
              justifyContent: "center",
              alignItems: "center",
              textWrap: "none",
              flexDirection: "column"
            }}
          >
            <Stack
              direction="row"
              gap="1.2rem"
              sx={{
                justifyContent: "center",
                width: "100%",
                textWrap: "none",
                flexWrap: "wrap-reverse"
              }}
            >
              {onViewItinerary && (
                <ColletteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewItinerary(tourDate);
                  }}
                  testId={`departure-date_${index}_view-itinerary-button`}
                  variant="outlined"
                  size="large"
                  sx={{
                    backgroundColor: "background.default"
                  }}
                  label={t("viewItinerary")}
                />
              )}
              {onBookNow && !isSoldOut && !isCallToBook && (
                <ColletteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onBookNow(tourDate);
                  }}
                  testId={`departure-date_${index}_book-now-button`}
                  color="primary"
                  size="large"
                  label={t("bookNow")}
                />
              )}
              {!isSoldOut && isCallToBook && (
                <ColletteButton
                  component="a"
                  href={`tel:${tourDate.callForDetailsPhoneNumber}`}
                  testId={`departure-date_${index}_call-to-book-button`}
                  color="primary"
                  size="large"
                  label={`${t("Call to Book")} ${tourDate.callForDetailsPhoneNumber}`}
                />
              )}
            </Stack>
            {onRequestQuote && (
              <ColletteTextButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onRequestQuote(tourDate);
                }}
                label={t("requestAQuote")}
                testId={`departure-date_${index}_request-quote-button`}
                color="primary"
                sx={{
                  fontSize: "1.4rem!important",
                  width: "fit-content",
                  fontWeight: 600,
                  "&:focus-visible": {
                    outline: "1px solid",
                    outlineColor: "primary.main"
                  }
                }}
              />
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default DepartureDateRow;
